/**
 * FilmPage — short cinematic "video" landing page that opens the pitch.
 *
 *  - 6 scenes, ~37 seconds total, auto-advancing
 *  - SVG + CSS animations only (no stock images, fully brand-clean)
 *  - Tone: confident, "the trade catches up" — Steve's voice
 *  - Background music supported (muted by default; toggle with M)
 *  - Screen-record to MP4 with any recorder
 *
 *  Route: /film    (public, no auth)
 *  Test:  https://servicesellpro.co.uk/film
 *
 *  Polish pass (Feb 2026):
 *   - Scene 0 counter is now JS-driven (was CSS content: race condition).
 *   - Scene 1 — depth pass: shadow, clock, second car, more papers.
 *   - Scene 4 — cascade tightened, alternating accents, numerals styled.
 *   - Cross-scene timing tuned for natural screen-record cadence.
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const SCENES = [
  { id: "hook",     ms: 4800 },
  { id: "problem",  ms: 7500 },
  { id: "cost",     ms: 4800 },
  { id: "pivot",    ms: 4200 },
  { id: "newway",   ms: 8500 },
  { id: "close",    ms: 5500 },
];

const NEW_STEPS = [
  { t: "AI vision drafts the quote",       icon: "eye" },
  { t: "Parts price auto-attaches",        icon: "tag" },
  { t: "Labour times auto-pulled",         icon: "clock" },
  { t: '"Fitted price" — one number',      icon: "pound" },
  { t: "SMS link to customer's phone",     icon: "phone" },
  { t: "Tap to approve",                   icon: "tap" },
  { t: "Workshop: green light",            icon: "check" },
  { t: "Service plan auto-pitched",        icon: "spark" },
  { t: "ROI tracked live",                 icon: "chart" },
];

const COUNTER_END = 35; // Scene 0 counter ticks 00:00 → 00:35

export default function FilmPage() {
  const [scene, setScene] = useState(0);
  const [muted, setMuted] = useState(true);
  const [counter, setCounter] = useState(0);
  const audioRef = useRef(null);

  // Auto-advance scenes
  useEffect(() => {
    if (scene >= SCENES.length) return;
    const t = setTimeout(() => setScene(s => s + 1), SCENES[scene].ms);
    return () => clearTimeout(t);
  }, [scene]);

  // Scene 0 counter — JS-driven via requestAnimationFrame (no setInterval-in-effect)
  const rafRef = useRef(null);
  useEffect(() => {
    if (scene !== 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    const start = performance.now();
    const duration = 3600;
    const tick = (now) => {
      const elapsed = now - start;
      const pct = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - pct, 3);
      setCounter(Math.round(eased * COUNTER_END));
      if (pct < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [scene]);

  // Music handling
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.32;
    if (!muted && scene > 0 && scene < SCENES.length) a.play().catch(() => {});
    else a.pause();
  }, [muted, scene]);

  // Keyboard: M to mute, R to restart, Space to skip
  useEffect(() => {
    const h = (e) => {
      if (e.key === "m" || e.key === "M") setMuted(m => !m);
      if (e.key === "r" || e.key === "R") setScene(0);
      if (e.key === " ") { e.preventDefault(); setScene(s => Math.min(s + 1, SCENES.length)); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const isFinished = scene >= SCENES.length;
  const counterDisplay = `00:${String(counter).padStart(2, "0")}`;

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden select-none"
      data-testid="film-page"
      onClick={() => isFinished && setScene(0)}
      style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
    >
      <audio ref={audioRef} src="/audio/cinematic.mp3" loop preload="auto" />

      {/* Subtle film grain overlay — adds cinematic feel */}
      <div className="absolute inset-0 pointer-events-none ssp-grain opacity-[0.06] z-50" />
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none ssp-vignette z-40" />

      {/* === SCENE 0 — HOOK === */}
      {scene === 0 && (
        <div className="absolute inset-0 grid place-items-center px-12 ssp-fade-in">
          <div className="text-center max-w-4xl">
            <div className="ssp-eyebrow text-emerald-400 ssp-eyebrow-in">ServiceSell PRO™</div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mt-6 mb-10 ssp-hook-h1">
              How long does it take<br />to put a quote in front<br />of a customer?
            </h1>
            <div
              className="text-7xl md:text-9xl font-mono font-bold text-rose-500 ssp-counter-pulse"
              data-testid="film-counter"
            >
              {counterDisplay}
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.4em] text-white/30">
              The trade&rsquo;s average · industry data
            </div>
          </div>
        </div>
      )}

      {/* === SCENE 1 — PROBLEM === */}
      {scene === 1 && (
        <div className="absolute inset-0 ssp-fade-in">
          {/* Background grid */}
          <div className="absolute inset-0 ssp-grid-bg opacity-30" />

          {/* Sky-to-floor wash */}
          <div className="absolute inset-x-0 top-0 h-1/2 ssp-sky-wash pointer-events-none" />

          {/* Animated SVG: workshop scene */}
          <svg viewBox="0 0 1600 900" className="absolute inset-0 w-full h-full">
            {/* Floor */}
            <line x1="0" y1="700" x2="1600" y2="700" stroke="#1f2937" strokeWidth="2" />
            <line x1="0" y1="704" x2="1600" y2="704" stroke="#0f172a" strokeWidth="1" />

            {/* Ramp + shadow */}
            <ellipse cx="520" cy="708" rx="170" ry="6" fill="#000" opacity="0.4" />
            <rect x="400" y="640" width="240" height="60" rx="4" fill="#1f2937" />
            <rect x="380" y="620" width="280" height="22" rx="3" fill="#374151" />

            {/* Background second car (faded) */}
            <g opacity="0.18" transform="translate(1050, 0)">
              <rect x="430" y="555" width="160" height="70" rx="16" fill="#94a3b8" />
              <rect x="455" y="538" width="110" height="36" rx="9" fill="#cbd5e1" />
              <circle cx="465" cy="625" r="14" fill="#0f0f0f" />
              <circle cx="555" cy="625" r="14" fill="#0f0f0f" />
            </g>

            {/* Foreground car — cartoon, on ramp */}
            <g className="ssp-car">
              <rect x="430" y="540" width="180" height="80" rx="22" fill="#22C55E" />
              <rect x="460" y="518" width="120" height="42" rx="12" fill="#16a34a" />
              {/* windscreen highlight */}
              <rect x="470" y="524" width="100" height="14" rx="4" fill="#86efac" opacity="0.7" />
              {/* headlight */}
              <circle cx="608" cy="572" r="6" fill="#fef9c3" />
              {/* wheels */}
              <circle cx="465" cy="620" r="17" fill="#0f0f0f" stroke="#4b5563" strokeWidth="2.5" />
              <circle cx="575" cy="620" r="17" fill="#0f0f0f" stroke="#4b5563" strokeWidth="2.5" />
              <circle cx="465" cy="620" r="6" fill="#374151" />
              <circle cx="575" cy="620" r="6" fill="#374151" />
            </g>

            {/* Floating paperwork (animated via CSS) */}
            <g className="ssp-paper p1">
              <rect x="0" y="0" width="60" height="80" fill="#fef3c7" stroke="#d97706" rx="2" />
              <line x1="8" y1="14" x2="52" y2="14" stroke="#d97706" />
              <line x1="8" y1="26" x2="52" y2="26" stroke="#d97706" />
              <line x1="8" y1="38" x2="40" y2="38" stroke="#d97706" />
              <line x1="8" y1="50" x2="48" y2="50" stroke="#d97706" />
            </g>
            <g className="ssp-paper p2">
              <rect x="0" y="0" width="60" height="80" fill="#fef3c7" stroke="#d97706" rx="2" />
              <line x1="8" y1="14" x2="52" y2="14" stroke="#d97706" />
              <line x1="8" y1="26" x2="40" y2="26" stroke="#d97706" />
              <line x1="8" y1="38" x2="50" y2="38" stroke="#d97706" />
            </g>
            <g className="ssp-paper p3">
              <rect x="0" y="0" width="60" height="80" fill="#fef3c7" stroke="#d97706" rx="2" />
              <line x1="8" y1="14" x2="52" y2="14" stroke="#d97706" />
              <line x1="8" y1="26" x2="52" y2="26" stroke="#d97706" />
              <line x1="8" y1="38" x2="30" y2="38" stroke="#d97706" />
            </g>
            <g className="ssp-paper p4">
              <rect x="0" y="0" width="60" height="80" fill="#fef3c7" stroke="#d97706" rx="2" />
              <line x1="8" y1="14" x2="44" y2="14" stroke="#d97706" />
              <line x1="8" y1="26" x2="52" y2="26" stroke="#d97706" />
            </g>

            {/* Wall clock — ticking */}
            <g transform="translate(220, 200)">
              <circle cx="0" cy="0" r="48" fill="#0f172a" stroke="#334155" strokeWidth="3" />
              <circle cx="0" cy="0" r="3" fill="#94a3b8" />
              {/* tick marks */}
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
                <line
                  key={deg}
                  x1="0" y1="-42" x2="0" y2="-36"
                  stroke="#475569" strokeWidth="2"
                  transform={`rotate(${deg})`}
                />
              ))}
              {/* hour hand */}
              <line x1="0" y1="0" x2="0" y2="-22" stroke="#cbd5e1" strokeWidth="3" strokeLinecap="round" />
              {/* minute hand — spinning fast */}
              <g className="ssp-clock-min">
                <line x1="0" y1="4" x2="0" y2="-34" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
              </g>
            </g>

            {/* Ringing phone */}
            <g className="ssp-phone-wrap" transform="translate(1280, 480)">
              <rect x="-32" y="-52" width="64" height="116" rx="11" fill="#111827" stroke="#374151" strokeWidth="3" />
              <rect x="-24" y="-42" width="48" height="84" rx="2" fill="#22C55E" opacity="0.22" />
              <circle cx="0" cy="58" r="3" fill="#374151" />
              {/* ring waves */}
              <g className="ssp-ring-waves">
                <circle cx="0" cy="6" r="48" fill="none" stroke="#22C55E" strokeWidth="2" opacity="0.35" />
                <circle cx="0" cy="6" r="72" fill="none" stroke="#22C55E" strokeWidth="2" opacity="0.2" />
              </g>
            </g>

            {/* Stack of paperwork on desk */}
            <g transform="translate(900, 660)" opacity="0.55">
              <rect x="-30" y="-3" width="80" height="6" fill="#fde68a" />
              <rect x="-26" y="-8" width="80" height="6" fill="#fbbf24" />
              <rect x="-32" y="-13" width="80" height="6" fill="#fcd34d" />
              <rect x="-28" y="-18" width="80" height="6" fill="#fde68a" />
            </g>
          </svg>

          <div className="absolute bottom-20 left-12 right-12 max-w-3xl ssp-slide-up-late">
            <div className="ssp-eyebrow text-rose-400">A day on the drive · today</div>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight mt-4">
              Paper job cards. Mis-tagged parts.<br />
              Ringing phones. Walk-ins.<br />
              <span className="text-rose-400">The customer waits.</span>
            </h2>
          </div>
        </div>
      )}

      {/* === SCENE 2 — COST === */}
      {scene === 2 && (
        <div className="absolute inset-0 grid place-items-center px-12 ssp-fade-in">
          {/* Background pound symbols drifting up */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[0,1,2,3,4,5,6,7].map((i) => (
              <div
                key={i}
                className="absolute text-rose-500/10 font-bold text-7xl ssp-pound-drift"
                style={{
                  left: `${(i * 12.5) + 4}%`,
                  bottom: "-80px",
                  animationDelay: `${i * 0.4}s`,
                }}
              >£</div>
            ))}
          </div>

          <div className="text-center relative">
            <div className="ssp-eyebrow text-rose-400">The cost</div>
            <div className="text-[140px] md:text-[220px] font-bold leading-none text-rose-500 ssp-big-pop tracking-tight">
              £11bn
            </div>
            <p className="text-2xl md:text-4xl font-bold mt-6 max-w-3xl mx-auto leading-tight">
              walks out of UK service drives every year.<br />
              <span className="text-white/55 text-lg md:text-2xl font-normal">Recommended work, customer-approved &mdash; lost to a broken process.</span>
            </p>
          </div>
        </div>
      )}

      {/* === SCENE 3 — PIVOT === */}
      {scene === 3 && (
        <div className="absolute inset-0 grid place-items-center px-12 ssp-fade-in">
          <div className="text-center max-w-4xl">
            <p className="text-3xl md:text-5xl font-bold text-white/40 leading-tight ssp-pivot-prefix">
              What if it took
            </p>
            <div className="text-[120px] md:text-[200px] font-bold leading-none text-emerald-400 ssp-pivot-pop mt-4 tracking-tight">
              30 seconds?
            </div>
            <div className="mt-10 ssp-pivot-line">
              <div className="inline-block h-px w-32 bg-emerald-400/40" />
            </div>
          </div>
        </div>
      )}

      {/* === SCENE 4 — NEW WAY === */}
      {scene === 4 && (
        <div className="absolute inset-0 ssp-fade-in px-8 md:px-16 py-12">
          <div className="ssp-eyebrow text-emerald-400 ssp-fade-in-fast">With ServiceSell PRO™</div>
          <h2 className="text-3xl md:text-5xl font-bold mt-3 mb-10 leading-tight ssp-fade-in-fast">
            Nine steps. <span className="text-emerald-400">Done automatically.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-7xl">
            {NEW_STEPS.map((step, idx) => (
              <div
                key={idx}
                className={`ssp-step-card rounded-xl border p-4 md:p-5 flex items-center gap-4 ${
                  idx % 3 === 1
                    ? "border-emerald-400/40 bg-emerald-500/8"
                    : "border-emerald-500/25 bg-emerald-500/[0.04]"
                }`}
                style={{ animationDelay: `${idx * 0.5 + 0.2}s` }}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-300 grid place-items-center font-mono font-bold text-sm shrink-0 border border-emerald-400/30">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="text-sm md:text-base font-semibold leading-tight text-white/95">{step.t}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-xs uppercase tracking-[0.35em] text-white/30 ssp-fade-in-fast" style={{ animationDelay: "5s" }}>
            ⏱  Quote to customer · under a minute
          </div>
        </div>
      )}

      {/* === SCENE 5 — CLOSE === */}
      {(scene === 5 || isFinished) && (
        <div className="absolute inset-0 grid place-items-center px-12 ssp-fade-in">
          {/* Faint radial glow */}
          <div className="absolute inset-0 pointer-events-none ssp-close-glow" />
          <div className="text-center max-w-4xl relative">
            <div className="ssp-eyebrow text-emerald-400">ServiceSell PRO™</div>
            <h1 className="text-5xl md:text-8xl font-bold tracking-tight leading-[1.05] mt-6 mb-10 ssp-close-pop">
              The trade<br /><span className="text-emerald-400">catches up.</span>
            </h1>
            <p className="text-base md:text-xl text-white/55 mb-10 ssp-close-sub">
              £1,000 onboarding. £1,500/mo or 3% commission. 12-month minimum. 30-day money-back.
            </p>
            {isFinished && (
              <div className="flex items-center justify-center gap-3 flex-wrap ssp-close-cta">
                <Link
                  to="/pilot"
                  className="px-7 py-3.5 rounded-md bg-emerald-500 text-black font-bold hover:bg-emerald-400 transition-colors shadow-[0_0_40px_rgba(34,197,94,0.35)]"
                  data-testid="film-cta-pilot"
                >
                  See the 30-day pilot →
                </Link>
                <button
                  onClick={(e) => { e.stopPropagation(); setScene(0); }}
                  className="px-7 py-3.5 rounded-md border border-white/20 text-white/85 font-medium hover:bg-white/5 transition-colors"
                  data-testid="film-replay"
                >
                  ↻ Replay
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom HUD — minimal */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] text-white/30 uppercase tracking-[0.2em] pointer-events-none z-50">
        <div>servicesellpro.co.uk</div>
        <div className="flex items-center gap-4">
          <span>{muted ? "[M] sound off" : "[M] sound on"}</span>
          <span>[Space] skip</span>
          <span>[R] replay</span>
        </div>
      </div>

      {/* Scene progress bar — bottom edge */}
      {!isFinished && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5 z-40">
          <div
            key={scene}
            className="h-full bg-emerald-400/70"
            style={{
              width: "100%",
              transformOrigin: "left",
              animation: `ssp-progress ${SCENES[scene].ms}ms linear forwards`,
            }}
          />
        </div>
      )}

      {/* === GLOBAL STYLES === */}
      <style>{`
        .ssp-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.3em;
          text-transform: uppercase;
        }
        .ssp-grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 56px 56px;
        }
        .ssp-sky-wash {
          background: radial-gradient(ellipse at 50% 0%, rgba(244,63,94,0.07), transparent 60%);
        }
        .ssp-grain {
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        .ssp-vignette {
          background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%);
        }

        /* Generic */
        @keyframes ssp-fade-in {
          from { opacity: 0; transform: scale(0.985); }
          to   { opacity: 1; transform: scale(1); }
        }
        .ssp-fade-in { animation: ssp-fade-in 0.7s ease-out both; }
        .ssp-fade-in-fast { animation: ssp-fade-in 0.5s ease-out both; }

        @keyframes ssp-slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ssp-slide-up-late { animation: ssp-slide-up 0.9s 1.6s ease-out both; }

        /* SCENE 0 */
        .ssp-eyebrow-in { animation: ssp-fade-in 0.6s 0.1s ease-out both; }
        @keyframes ssp-hook-h1 {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ssp-hook-h1 { animation: ssp-hook-h1 0.9s 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }

        @keyframes ssp-counter-pulse {
          0%, 100% { transform: scale(1); text-shadow: 0 0 0 rgba(244,63,94,0); }
          50%      { transform: scale(1.015); text-shadow: 0 0 32px rgba(244,63,94,0.45); }
        }
        .ssp-counter-pulse {
          animation: ssp-counter-pulse 1.4s ease-in-out infinite;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.02em;
        }

        /* SCENE 1 — car rise */
        @keyframes ssp-car-rise {
          from { transform: translateY(60px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .ssp-car { animation: ssp-car-rise 1.1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both; transform-origin: center; }

        @keyframes ssp-paper-float-1 {
          0%   { transform: translate(180px, 200px) rotate(-15deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate(1100px, 380px) rotate(25deg); opacity: 0.25; }
        }
        @keyframes ssp-paper-float-2 {
          0%   { transform: translate(220px, 320px) rotate(8deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate(1140px, 260px) rotate(-20deg); opacity: 0.25; }
        }
        @keyframes ssp-paper-float-3 {
          0%   { transform: translate(160px, 260px) rotate(-5deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate(1180px, 320px) rotate(15deg); opacity: 0.25; }
        }
        @keyframes ssp-paper-float-4 {
          0%   { transform: translate(250px, 180px) rotate(12deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate(1080px, 460px) rotate(-30deg); opacity: 0.25; }
        }
        .ssp-paper.p1 { animation: ssp-paper-float-1 4.2s 0.3s ease-in-out infinite; }
        .ssp-paper.p2 { animation: ssp-paper-float-2 4.7s 0.7s ease-in-out infinite; }
        .ssp-paper.p3 { animation: ssp-paper-float-3 5.0s 1.1s ease-in-out infinite; }
        .ssp-paper.p4 { animation: ssp-paper-float-4 4.4s 1.5s ease-in-out infinite; }

        @keyframes ssp-phone-shake {
          0%, 88%, 100% { transform: translate(1280px, 480px) rotate(0deg); }
          92%           { transform: translate(1278px, 480px) rotate(-7deg); }
          96%           { transform: translate(1282px, 480px) rotate(7deg); }
        }
        .ssp-phone-wrap { animation: ssp-phone-shake 1.8s ease-in-out infinite; transform-origin: center; }
        @keyframes ssp-ring-waves {
          0%, 100% { opacity: 0; transform: scale(0.7); }
          50%      { opacity: 1; transform: scale(1.05); }
        }
        .ssp-ring-waves { animation: ssp-ring-waves 1.8s ease-in-out infinite; transform-origin: center; }

        @keyframes ssp-clock-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .ssp-clock-min { animation: ssp-clock-spin 3.5s linear infinite; transform-origin: 0 0; }

        /* SCENE 2 */
        @keyframes ssp-big-pop {
          0%   { opacity: 0; transform: scale(0.4); }
          70%  { opacity: 1; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        .ssp-big-pop { animation: ssp-big-pop 1.1s cubic-bezier(0.34, 1.56, 0.64, 1) both; }

        @keyframes ssp-pound-drift {
          0%   { transform: translateY(0) rotate(-12deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(-1000px) rotate(28deg); opacity: 0; }
        }
        .ssp-pound-drift { animation: ssp-pound-drift 5.5s ease-out infinite; }

        /* SCENE 3 */
        @keyframes ssp-pivot-pop {
          0%   { opacity: 0; letter-spacing: 0.15em; transform: scale(0.6); }
          80%  { opacity: 1; letter-spacing: -0.02em; transform: scale(1.04); }
          100% { opacity: 1; letter-spacing: -0.02em; transform: scale(1); }
        }
        .ssp-pivot-pop { animation: ssp-pivot-pop 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both; }
        .ssp-pivot-prefix { animation: ssp-fade-in 0.6s ease-out both; }
        @keyframes ssp-pivot-line {
          from { transform: scaleX(0); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
        .ssp-pivot-line div { animation: ssp-pivot-line 0.9s 1.6s cubic-bezier(0.16, 1, 0.3, 1) both; transform-origin: center; }

        /* SCENE 4 */
        @keyframes ssp-step-in {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ssp-step-card { animation: ssp-step-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }

        /* SCENE 5 */
        @keyframes ssp-close-pop {
          0%   { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ssp-close-pop { animation: ssp-close-pop 1.2s ease-out 0.2s both; }
        .ssp-close-sub { animation: ssp-fade-in 0.8s 1.0s ease-out both; }
        .ssp-close-cta { animation: ssp-fade-in 0.6s 1.4s ease-out both; }
        .ssp-close-glow {
          background: radial-gradient(ellipse at center, rgba(34,197,94,0.10), transparent 60%);
        }

        /* Progress bar */
        @keyframes ssp-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}
